import { PayloadAction } from "@reduxjs/toolkit";
import { put, call, takeLatest, all, select } from "redux-saga/effects";
import nameof from "ts-nameof.macro";

import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { isLocalizable, ILocalizable } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { securityMessages } from "ui/screens/security/security.localization";
import { tryExtractLocalizableApiErrorMessage, extractApiErrorMessageContent } from "ui/shared/models/common";

import { IGrantRoleData, IGrantRoleFormState } from "./grant-role/GrantRole.state";
import { securityRoleMembersActions } from "./SecurityRoleMembers.actions";
import { getSecurityRoleMembersByTenant, getSecurityRoleMembersByEnterprise, grantRole, revokeSecurityRole } from "./SecurityRoleMembers.api";
import { ISecurityRoleMembersResponse, IGrantRoleRequest } from "./SecurityRoleMembers.api.types";
import { ISecurityRoleMember, ISecurityRoleMembersState } from "./SecurityRoleMembers.state";

function* loadSecurityRoleMembersExecute() {
  try {
    const state: ISecurityRoleMembersState = yield select((state: { securityRoleMembers: ISecurityRoleMembersState }) => state.securityRoleMembers);
    const securityRoleId = state.filter?.securityRole?.securityRoleId;
    const tenantId = state.filter?.tenant?.tenantId;
    const enterpriseId = state.filter?.enterprise?.enterpriseId;

    let response: ISecurityRoleMembersResponse[] = [];

    if (!securityRoleId || (!tenantId && !enterpriseId)) {
      yield put(securityRoleMembersActions.notify(notification.warning().useMessageId(securityMessages.filter("invalidRequiredFieldState"))));
    } else {
      if (tenantId) {
        response = yield call(getSecurityRoleMembersByTenant, { tenantId, securityRoleId });
      } else {
        response = yield call(getSecurityRoleMembersByEnterprise, { enterpriseId: enterpriseId!, securityRoleId: securityRoleId });
      }

      const securityRoles = response.map<ISecurityRoleMember>(e => ({
        userId: e.account.accountId,
        user: e.account.name,
        enterpriseWide: e.enterpriseWide,
        validDateFrom: e.validDateFrom,
        validDateTo: e.validDateTo,
        tenantName: e.tenant.name,
        tenantId: e.tenant.tenantId
      }));

      yield put(securityRoleMembersActions.loadSecurityRoleMembersCompleted(securityRoles));
    }
  } catch (e) {
    yield put(securityRoleMembersActions.notify(notification.error().useMessageId(securityMessages.error("loadSecurityRolesFailed"))));
  }
}

function* applyFilterExecute() {
  yield put(securityRoleMembersActions.loadSecurityRoleMembers());
}

function* revokeSecurityRoleExecute() {
  const state: ISecurityRoleMembersState = yield select((state: { securityRoleMembers: ISecurityRoleMembersState }) => state.securityRoleMembers);
  const selectedMembers = state.selectedItems;
  const securityRoleId = state.filter?.securityRole?.securityRoleId;
  const tenantId = state.filter?.tenant?.tenantId;
  const enterpriseId = state.filter?.enterprise?.enterpriseId;

  if (!securityRoleId || (!tenantId && !enterpriseId)) {
    yield put(securityRoleMembersActions.revokeSecurityRoleFailed(notification.warning().useMessageId(securityMessages.filter("invalidRequiredFieldState"))));
  } else {
    const request = selectedMembers.map(sm => ({ securityRoleId, accountId: sm.userId, tenantId: sm.tenantId }));

    try {
      yield call(revokeSecurityRole, request);
      yield put(securityRoleMembersActions.loadSecurityRoleMembers());
      yield put(securityRoleMembersActions.revokeSecurityRoleCompleted(notification.success().useMessageId(securityMessages.info("securityRoleRevocationCompleted"))));
    } catch (error) {
      const messages: ILocalizable[] = [{ messageId: securityMessages.error("securityRoleRevocationFailed") }];

      if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
        for (const model of error.data) {
          const errorMessage = extractApiErrorMessageContent(model.error);

          if (isLocalizable(errorMessage) && !messages.some(m => m.messageId.value === errorMessage.messageId.value)) {
            messages.push(errorMessage);
          }
        }
      }

      yield put(securityRoleMembersActions.revokeSecurityRoleFailed(notification.error().useMessages(messages)));
    }
  }
}

function* grantRoleExecute({ payload }: PayloadAction<IGrantRoleData>) {
  try {
    yield call(grantRole, {
      accountId: payload.account.accountId,
      enterpriseWide: payload.enterpriseWide,
      securityRoleId: payload.securityRole.securityRoleId,
      tenantId: payload.tenant.tenantId,
      validDateFrom: payload.validDateFrom?.toString(),
      validDateTo: payload.validDateTo?.toString()
    } as IGrantRoleRequest);

    yield put(securityRoleMembersActions.grantRoleCompleted());
    yield put(securityRoleMembersActions.loadSecurityRoleMembers());
    yield put(securityRoleMembersActions.notify(notification.success().useMessageId(securityMessages.info("grantRoleCompleted"))));
  } catch (error) {
    let errorNotification = notification.error().useMessageId(securityMessages.info("grantRoleFailed"));
    let canComplete = true;

    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const badRequestError = tryExtractLocalizableApiErrorMessage(error.data)?.localizable;

      if (badRequestError) {
        canComplete = false;
        errorNotification = {
          ...notification.error().useMessage(badRequestError),
          areaName: nameof<IGrantRoleFormState>()
        };
      }
    }

    if (canComplete) {
      yield put(securityRoleMembersActions.grantRoleCompleted());
    }

    yield put(securityRoleMembersActions.notify(errorNotification));
  }
}

export function* securityRoleMembersSaga() {
  yield all([
    yield takeLatest(securityRoleMembersActions.loadSecurityRoleMembers.toString(), loadSecurityRoleMembersExecute),
    yield takeLatest(securityRoleMembersActions.applyFilter.toString(), applyFilterExecute),
    yield takeLatest(securityRoleMembersActions.grantRole.toString(), grantRoleExecute),
    yield takeLatest(securityRoleMembersActions.revokeSecurityRole.toString(), revokeSecurityRoleExecute)
  ]);
}
